import React, { useState } from 'react';
import { Printer, RotateCcw } from 'lucide-react';
import { useYear } from '../context/YearContext';

/* ── Progress-card color palette ─────────────────────────────────────────────── */
const C = {
  navy:   '#1a3a6b',
  mid:    '#2756a0',
  red:    '#c0392b',
  bg:     '#f0f4f8',
  label:  '#374151',
  muted:  '#6b7280',
  border: '#1e3a6e',
  light:  '#deeaf5',
};

/* ── Form state (student + office-use) ───────────────────────────────────────── */
const EMPTY = {
  /* student */
  student_name: '', dob_dd: '', dob_mm: '', dob_yy: '', dob_words: '',
  aadhar: '', gender: '',
  nationality: '', religion: '', pen: '',
  mother_tongue: '', caste: '', sub_caste: '', community: '',
  last_class: '', result: '', medium: '',
  prev_school: '',
  admission_class: '', student_type: '',
  id_marks: '',
  /* parent */
  mother_name: '',
  father_name: '', guardian_name: '',
  qualification: '',
  occupation: '', annual_income: '',
  mobile: '', email: '',
  /* present address */
  p_hno: '', p_area: '', p_village: '',
  p_mandal: '', p_dist: '', p_state: '', p_pin: '',
  /* permanent address */
  pm_hno: '', pm_area: '', pm_village: '',
  pm_mandal: '', pm_dist: '', pm_state: '', pm_pin: '',
  /* office use only */
  o_st_name: '', o_class: '', o_adm_no: '',
  o_receipt_no: '', o_date: '', o_amount: '',
  o_referral: '', o_referral_type: '',
  o_cert_tc: false, o_cert_rs: false, o_cert_bon: false,
  o_cert_aadhar: false, o_cert_birth: false,
};

/* ── Print-HTML builder (single A4 page) ─────────────────────────────────────── */
function buildPrintHTML(f, schoolName, logoUrl, sy1, sy2) {
  const v = (k) => f[k] || '';

  /* tiny helpers that emit inline HTML */
  const lbl = (t) =>
    `<span style="font-size:9.5px;color:#374151;white-space:nowrap;font-weight:500;">${t}</span>`;
  const col = `<span style="margin:0 1px;">:</span>`;
  const ul  = (val, w) =>
    `<span style="display:inline-block;border-bottom:1px solid #555;min-width:${w || 70}px;` +
    `height:12px;vertical-align:bottom;padding:0 2px;font-size:9.5px;color:#1a3a6b;">${val}</span>`;
  const row = (...cells) =>
    `<div style="display:flex;align-items:flex-end;gap:4px;margin:2.5px 0;flex-wrap:nowrap;">${cells.join('')}</div>`;
  const cb  = (checked, label) =>
    `<span style="display:inline-flex;align-items:center;gap:2px;white-space:nowrap;">` +
    `<span style="width:10px;height:10px;border:1px solid #333;display:inline-flex;align-items:center;` +
    `justify-content:center;font-size:8px;flex-shrink:0;">${checked ? '&#10003;' : ''}</span>` +
    `<span style="font-size:9.5px;">${label}</span></span>`;
  const sec = (title) =>
    `<div style="background:#1a3a6b;color:#fff;padding:2px 7px;font-size:9.5px;font-weight:700;` +
    `margin:5px 0 3px;border-left:3px solid #c0392b;letter-spacing:.3px;` +
    `-webkit-print-color-adjust:exact;print-color-adjust:exact;">${title}</div>`;
  const sp  = (n = 6) => `<span style="margin-left:${n}px;flex-shrink:0;"></span>`;

  const logoHtml = logoUrl
    ? `<img src="${logoUrl}" style="width:46px;height:46px;object-fit:contain;background:#fff;` +
      `border-radius:50%;padding:3px;box-shadow:0 1px 6px rgba(0,0,0,.25);" />`
    : `<div style="width:46px;height:46px;border-radius:50%;background:rgba(255,255,255,.2);` +
      `border:1px solid rgba(255,255,255,.4);"></div>`;

  return `<!DOCTYPE html><html><head>
<meta charset="utf-8"/>
<title>Admission Form — ${schoolName || 'School'}</title>
<style>
  *{box-sizing:border-box;margin:0;padding:0;}
  body{font-family:'Segoe UI',Arial,sans-serif;background:#fff;color:#000;}
  @page{size:A4 portrait;margin:7mm 9mm;}
  .page{display:flex;flex-direction:column;}
  .body{display:flex;flex-direction:column;}
  @media print{
    body{margin:0;}
    .page{width:100%!important;height:calc(297mm - 14mm)!important;}
    .body{flex:1;}
    div[style*="background:#1a3a6b"],div[style*="background:#c0392b"],
    div[style*="background:linear-gradient"]{
      -webkit-print-color-adjust:exact!important;
      print-color-adjust:exact!important;
    }
  }
</style>
</head><body><div class="page" style="width:210mm;background:#fff;">

<!-- Header -->
<div style="background:linear-gradient(135deg,#1a3a6b 0%,#2756a0 100%);padding:7px 14px 6px;
  display:flex;align-items:center;gap:12px;-webkit-print-color-adjust:exact;print-color-adjust:exact;">
  ${logoHtml}
  <div style="flex:1;text-align:center;color:#fff;">
    <div style="font-size:18px;font-weight:900;letter-spacing:1px;">${schoolName || 'School Name'}</div>
    <div style="font-size:8px;opacity:.85;margin-top:1px;">Recognised by the Govt. of Telangana</div>
    <div style="display:flex;justify-content:center;gap:20px;font-size:7.5px;opacity:.7;margin-top:1px;">
      <span>R.C. No.: __________</span><span>Affiliation No.: __________</span>
    </div>
  </div>
  <div style="width:52px;height:64px;border:1px solid rgba(255,255,255,.5);
    display:flex;align-items:center;justify-content:center;
    color:rgba(255,255,255,.75);font-size:9px;text-align:center;">Photo</div>
</div>

<!-- Title -->
<div style="background:#c0392b;color:#fff;text-align:center;padding:4px;font-size:12px;
  font-weight:800;letter-spacing:2px;-webkit-print-color-adjust:exact;print-color-adjust:exact;">
  ADMISSION FORM &nbsp;|&nbsp; SESSION ${sy1} &ndash; ${sy2}
</div>

<!-- Body -->
<div class="body" style="padding:5px 12px 6px;">

${sec('&#9632; STUDENT PERSONAL DETAILS')}
${row(lbl("Student's Name") + col, ul(v('student_name'), 280), `<span style="font-size:8px;color:#555;white-space:nowrap;">(Fill in Block Letters)</span>`)}
${row(lbl('Date of Birth') + col, ul(v('dob_dd'), 24), '<span>/</span>', ul(v('dob_mm'), 24), '<span>/</span>', ul(v('dob_yy'), 44), sp(4), `<span style="font-size:8px;color:#555;">(in words)</span>`, ul(v('dob_words')))}
${row(lbl('Aadhar Number') + col, ul(v('aadhar'), 120), sp(10), lbl('Gender') + col, cb(v('gender') === 'Boy', 'Boy'), sp(6), cb(v('gender') === 'Girl', 'Girl'))}
${row(lbl('Nationality') + col, ul(v('nationality'), 85), sp(), lbl('Religion') + col, ul(v('religion'), 85), sp(), lbl('PEN') + col, ul(v('pen')))}
${row(lbl('Mother Tongue') + col, ul(v('mother_tongue'), 65), sp(4), lbl('Caste') + col, ul(v('caste'), 55), sp(4), lbl('Sub Caste') + col, ul(v('sub_caste'), 56), sp(4), lbl('Community') + col, ul(v('community')))}
${row(lbl('Last Class Attended') + col, ul(v('last_class'), 65), sp(), lbl('Result') + col, ul(v('result'), 55), sp(), lbl('Medium') + col, ul(v('medium')))}
${row(lbl('School Name &amp; Place') + col, ul(v('prev_school')))}
${row(lbl('Class in which admission sought') + col, ul(v('admission_class'), 95), sp(10), lbl('Residential') + col, cb(v('student_type') === 'residential', ''), sp(8), lbl('Day Scholar') + col, cb(v('student_type') === 'day_scholar', ''))}
${row(lbl('Identification Marks') + col, ul(v('id_marks')))}

${sec('&#9632; PARENT\'S DETAILS')}
${row(lbl("Mother's Name") + col, ul(v('mother_name')))}
${row(lbl("Father's Name") + col, ul(v('father_name')), sp(8), lbl('Guardian Name') + col, ul(v('guardian_name')))}
${row(lbl('Qualification') + col, cb(v('qualification') === 'below_ssc', 'Below SSC'), sp(), cb(v('qualification') === 'ssc', 'SSC'), sp(), cb(v('qualification') === 'above_ssc', 'Above SSC'))}
${row(lbl('Occupation') + col, ul(v('occupation')), sp(8), lbl('Annual Income') + col, ul(v('annual_income')))}
${row(lbl('Mobile No.') + col, ul(v('mobile')), sp(8), lbl('Email') + col, ul(v('email')))}

${sec('&#9632; ADDRESS')}
<div style="font-weight:600;font-size:9.5px;margin:2px 0 1px;">Present Address</div>
${row(lbl('H.No.') + col, ul(v('p_hno'), 62), sp(), lbl('Area') + col, ul(v('p_area'), 82), sp(), lbl('Village') + col, ul(v('p_village')))}
${row(lbl('Mandal') + col, ul(v('p_mandal'), 74), sp(), lbl('Dist.') + col, ul(v('p_dist'), 74), sp(), lbl('State') + col, ul(v('p_state'), 74), sp(), lbl('Pin') + col, ul(v('p_pin'), 48))}
<div style="font-weight:600;font-size:9.5px;margin:3px 0 1px;">Permanent Address</div>
${row(lbl('H.No.') + col, ul(v('pm_hno'), 62), sp(), lbl('Area') + col, ul(v('pm_area'), 82), sp(), lbl('Village') + col, ul(v('pm_village')))}
${row(lbl('Mandal') + col, ul(v('pm_mandal'), 74), sp(), lbl('Dist.') + col, ul(v('pm_dist'), 74), sp(), lbl('State') + col, ul(v('pm_state'), 74), sp(), lbl('Pin') + col, ul(v('pm_pin'), 48))}

<div style="flex:1;min-height:6px;"></div>
<div style="font-size:8px;color:#333;font-style:italic;margin:4px 0 3px;">
  <strong>Note :</strong> I declared that the statement given above is true and correct to my best of knowledge.
</div>

<div style="display:flex;justify-content:space-between;margin-top:8px;font-size:9.5px;">
  <div style="text-align:center;">
    <div style="border-top:1px solid #333;width:190px;margin:16px 0 2px;"></div>
    <div>Signature of the Father / Mother / Guardian</div>
  </div>
  <div style="text-align:center;">
    <div style="border-top:1px solid #333;width:110px;margin:16px auto 2px;"></div>
    <div>Principal</div>
  </div>
</div>

<!-- Office use only -->
<div style="border:1.5px solid #1a3a6b;margin-top:6px;padding:4px 7px;
  -webkit-print-color-adjust:exact;print-color-adjust:exact;">
  <div style="text-align:center;font-weight:700;font-size:10px;color:#1a3a6b;
    border-bottom:1px solid #aaa;padding-bottom:2px;margin-bottom:4px;letter-spacing:1px;">
    — FOR OFFICE USE ONLY —
  </div>
  ${row(lbl('St. Name') + col, ul(v('o_st_name'), 140), sp(8), lbl('Class') + col, ul(v('o_class'), 48), sp(8), lbl('Admission No.') + col, ul(v('o_adm_no'), 68))}
  ${row(lbl('Receipt No.') + col, ul(v('o_receipt_no'), 78), sp(), lbl('Date') + col, ul(v('o_date'), 78), sp(), lbl('Amount') + col, ul(v('o_amount')))}
  ${row(lbl('Referral Name') + col, ul(v('o_referral'), 110), sp(12), cb(v('o_referral_type') === 'parent', 'Parent'), sp(6), cb(v('o_referral_type') === 'staff', 'Staff'), sp(6), cb(v('o_referral_type') === 'incharge', 'Add Incharge'))}
  <div style="margin-top:3px;font-size:9.5px;">
    <span style="font-weight:600;">Certificates to be enclosed :</span>
    <div style="display:flex;flex-wrap:wrap;gap:3px 12px;margin-top:2px;margin-left:3px;">
      ${cb(!!f.o_cert_tc,     'Transfer Certificate')}
      ${cb(!!f.o_cert_rs,     'Record Sheet No. &amp; Date')}
      ${cb(!!f.o_cert_bon,    'Bonafide')}
      ${cb(!!f.o_cert_aadhar, 'Aadhar Card Xerox')}
      ${cb(!!f.o_cert_birth,  'Birth Certificate')}
    </div>
  </div>
  <div style="display:flex;justify-content:space-between;margin-top:8px;font-size:9.5px;">
    <div><div style="border-top:1px solid #333;width:110px;margin-bottom:2px;"></div><div>Incharge</div></div>
    <div style="text-align:center;"><div style="border-top:1px solid #333;width:100px;margin:0 auto 2px;"></div><div>Principal</div></div>
  </div>
</div>

</div></div></body></html>`;
}

/* ── Screen input styles ─────────────────────────────────────────────────────── */
const iSt = {
  flex: 1, border: 'none', borderBottom: `1.5px solid ${C.border}`,
  outline: 'none', fontSize: 11, color: C.navy, background: 'transparent',
  padding: '1px 3px', minWidth: 28, height: 20,
  fontFamily: "'Segoe UI', Arial, sans-serif",
};

function SectionHdr({ children }) {
  return (
    <div style={{
      background: C.navy, color: '#fff', padding: '4px 9px',
      fontSize: 10.5, fontWeight: 700, margin: '8px 0 4px',
      borderLeft: `4px solid ${C.red}`, letterSpacing: 0.4,
    }}>{children}</div>
  );
}
function SubHdr({ children }) {
  return <div style={{ fontWeight: 600, fontSize: 10.5, color: C.navy, margin: '4px 0 2px' }}>{children}</div>;
}
function FRow({ children, style }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 5, margin: '4px 0', flexWrap: 'nowrap', ...style }}>
      {children}
    </div>
  );
}
function Lbl({ children }) {
  return <span style={{ fontSize: 10.5, color: C.label, whiteSpace: 'nowrap', flexShrink: 0, fontWeight: 500 }}>{children}</span>;
}
function Col() { return <span style={{ margin: '0 1px', flexShrink: 0 }}>:</span>; }
function Inp({ value, onChange, style, ...rest }) {
  return <input value={value} onChange={onChange} style={{ ...iSt, ...style }} {...rest} />;
}
function Chk({ label, checked, onChange }) {
  return (
    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer', fontSize: 10.5, color: C.label, whiteSpace: 'nowrap', flexShrink: 0 }}>
      <span style={{
        width: 12, height: 12, border: `1.5px solid ${checked ? C.navy : '#9ca3af'}`,
        background: checked ? C.navy : '#fff', display: 'inline-flex',
        alignItems: 'center', justifyContent: 'center', flexShrink: 0, borderRadius: 2,
      }}>
        {checked && <span style={{ color: '#fff', fontSize: 8, fontWeight: 900 }}>✓</span>}
      </span>
      {label}
      <input type="checkbox" checked={checked} onChange={onChange} style={{ display: 'none' }} />
    </label>
  );
}
function Sp({ n = 10 }) { return <span style={{ marginLeft: n, flexShrink: 0 }} />; }

/* ── Page component ──────────────────────────────────────────────────────────── */
export default function AdmissionFormPrint({ schoolName, schoolLogoUrl }) {
  const { year }        = useYear();
  const [form, setForm] = useState(EMPTY);

  const parts = (year || '').split('-');
  const sy1   = parts[0] || '20__';
  const sy2   = parts[1] ? `20${parts[1]}` : '20__';

  const set    = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggle = (k, v) => () => setForm((f) => ({ ...f, [k]: f[k] === v ? '' : v }));
  const tick   = (k) => () => setForm((f) => ({ ...f, [k]: !f[k] }));

  const handlePrint = () => {
    const html = buildPrintHTML(form, schoolName, schoolLogoUrl, sy1, sy2);
    const win  = window.open('', '_blank', 'width=960,height=760');
    win.document.write(html);
    win.document.close();
    win.focus();
    setTimeout(() => { win.print(); win.close(); }, 900);
  };

  const handleReset = () => {
    if (window.confirm('Clear all fields and start fresh?')) setForm(EMPTY);
  };

  return (
    <div style={{ padding: '16px 20px', background: C.bg, minHeight: '100%', fontFamily: "'Segoe UI', Arial, sans-serif" }}>

      {/* ── Toolbar ─────────────────────────────────────────────────────────── */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        background: '#fff', border: '1px solid #d1d5db', borderRadius: 8,
        padding: '11px 18px', marginBottom: 16,
        boxShadow: '0 1px 4px rgba(0,0,0,.07)',
      }}>
        <div>
          <div style={{ fontSize: 17, fontWeight: 700, color: C.navy }}>Admission Form</div>
          <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>
            Fill in the details, then click <strong>Print</strong> to get the A4 form
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={handleReset} className="btn btn-secondary" style={{ display: 'flex', alignItems: 'center', gap: 5 }} title="Clear all fields">
            <RotateCcw size={13} /> Clear
          </button>
          <button onClick={handlePrint} className="btn btn-success" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Printer size={14} /> Print / Download PDF
          </button>
        </div>
      </div>

      {/* ── A4 preview card ─────────────────────────────────────────────────── */}
      <div style={{
        background: '#fff', border: `2px solid ${C.border}`, borderRadius: 6,
        maxWidth: 820, margin: '0 auto', overflow: 'hidden',
        boxShadow: '0 2px 12px rgba(0,0,0,.12)',
      }}>

        {/* Header */}
        <div style={{
          background: `linear-gradient(135deg, ${C.navy} 0%, ${C.mid} 100%)`,
          padding: '10px 18px', display: 'flex', alignItems: 'center', gap: 14,
        }}>
          {schoolLogoUrl ? (
            <img src={schoolLogoUrl} alt="Logo" style={{
              width: 56, height: 56, objectFit: 'contain',
              background: '#fff', borderRadius: '50%', padding: 4,
              boxShadow: '0 2px 8px rgba(0,0,0,.25)', flexShrink: 0,
            }} />
          ) : (
            <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'rgba(255,255,255,.18)', border: '1px solid rgba(255,255,255,.4)', flexShrink: 0 }} />
          )}
          <div style={{ flex: 1, textAlign: 'center', color: '#fff' }}>
            <div style={{ fontSize: 19, fontWeight: 900, letterSpacing: 1, textShadow: '0 1px 4px rgba(0,0,0,.3)' }}>
              {schoolName || 'School Name'}
            </div>
            <div style={{ fontSize: 9, opacity: 0.85, marginTop: 2 }}>Recognised by the Govt. of Telangana</div>
            <div style={{ display: 'flex', justifyContent: 'center', gap: 24, fontSize: 8.5, opacity: 0.7, marginTop: 2 }}>
              <span>R.C. No.: __________</span>
              <span>Affiliation No.: __________</span>
            </div>
          </div>
          <div style={{
            width: 58, height: 74, border: '1px solid rgba(255,255,255,.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'rgba(255,255,255,.7)', fontSize: 10, flexShrink: 0,
          }}>Photo</div>
        </div>

        {/* Red title banner */}
        <div style={{
          background: C.red, color: '#fff', textAlign: 'center',
          padding: '5px 0', fontSize: 12.5, fontWeight: 800, letterSpacing: 2,
        }}>
          ADMISSION FORM &nbsp;|&nbsp; SESSION {sy1} &ndash; {sy2}
        </div>

        {/* ── Form body ─────────────────────────────────────────────────────── */}
        <div style={{ padding: '10px 16px 14px' }}>

          {/* ══ STUDENT PERSONAL DETAILS ═══════════════════════════════════ */}
          <SectionHdr>&#9632; STUDENT PERSONAL DETAILS</SectionHdr>

          <FRow>
            <Lbl>Student's Name</Lbl><Col />
            <Inp value={form.student_name} onChange={set('student_name')} placeholder="Full name in block letters" />
            <span style={{ fontSize: 9, color: C.muted, whiteSpace: 'nowrap' }}>(Block Letters)</span>
          </FRow>
          <FRow>
            <Lbl>Date of Birth</Lbl><Col />
            <Inp value={form.dob_dd}    onChange={set('dob_dd')}    placeholder="DD"   style={{ width: 36, flex: 'none' }} maxLength={2} />
            <span>/</span>
            <Inp value={form.dob_mm}    onChange={set('dob_mm')}    placeholder="MM"   style={{ width: 36, flex: 'none' }} maxLength={2} />
            <span>/</span>
            <Inp value={form.dob_yy}    onChange={set('dob_yy')}    placeholder="YYYY" style={{ width: 52, flex: 'none' }} maxLength={4} />
            <span style={{ fontSize: 9, color: C.muted, whiteSpace: 'nowrap' }}>(in words)</span>
            <Inp value={form.dob_words} onChange={set('dob_words')} placeholder="Date in words" />
          </FRow>
          <FRow>
            <Lbl>Aadhar Number</Lbl><Col />
            <Inp value={form.aadhar} onChange={set('aadhar')} placeholder="12 digits" style={{ width: 140, flex: 'none' }} maxLength={12} />
            <Sp n={16} /><Lbl>Gender</Lbl><Col />
            <Chk label="Boy"  checked={form.gender === 'Boy'}  onChange={toggle('gender', 'Boy')} />
            <Sp /><Chk label="Girl" checked={form.gender === 'Girl'} onChange={toggle('gender', 'Girl')} />
          </FRow>
          <FRow>
            <Lbl>Nationality</Lbl><Col /><Inp value={form.nationality} onChange={set('nationality')} placeholder="Indian" style={{ width: 90, flex: 'none' }} />
            <Sp /><Lbl>Religion</Lbl><Col /><Inp value={form.religion} onChange={set('religion')} style={{ width: 90, flex: 'none' }} />
            <Sp /><Lbl>PEN</Lbl><Col /><Inp value={form.pen} onChange={set('pen')} />
          </FRow>
          <FRow>
            <Lbl>Mother Tongue</Lbl><Col /><Inp value={form.mother_tongue} onChange={set('mother_tongue')} style={{ width: 72, flex: 'none' }} />
            <Sp n={6} /><Lbl>Caste</Lbl><Col /><Inp value={form.caste} onChange={set('caste')} style={{ width: 64, flex: 'none' }} />
            <Sp n={6} /><Lbl>Sub Caste</Lbl><Col /><Inp value={form.sub_caste} onChange={set('sub_caste')} style={{ width: 64, flex: 'none' }} />
            <Sp n={6} /><Lbl>Community</Lbl><Col /><Inp value={form.community} onChange={set('community')} />
          </FRow>
          <FRow>
            <Lbl>Last Class Attended</Lbl><Col /><Inp value={form.last_class} onChange={set('last_class')} style={{ width: 72, flex: 'none' }} />
            <Sp /><Lbl>Result</Lbl><Col /><Inp value={form.result} onChange={set('result')} placeholder="Pass/Fail" style={{ width: 68, flex: 'none' }} />
            <Sp /><Lbl>Medium</Lbl><Col /><Inp value={form.medium} onChange={set('medium')} placeholder="Telugu/English" />
          </FRow>
          <FRow>
            <Lbl>School Name &amp; Place</Lbl><Col />
            <Inp value={form.prev_school} onChange={set('prev_school')} placeholder="Previous school name and place" />
          </FRow>
          <FRow>
            <Lbl>Class in which admission sought</Lbl><Col />
            <Inp value={form.admission_class} onChange={set('admission_class')} placeholder="e.g. VI" style={{ width: 80, flex: 'none' }} />
            <Sp n={16} />
            <Chk label="Residential" checked={form.student_type === 'residential'} onChange={toggle('student_type', 'residential')} />
            <Sp /><Chk label="Day Scholar" checked={form.student_type === 'day_scholar'} onChange={toggle('student_type', 'day_scholar')} />
          </FRow>
          <FRow>
            <Lbl>Identification Marks</Lbl><Col />
            <Inp value={form.id_marks} onChange={set('id_marks')} placeholder="Visible identification marks" />
          </FRow>

          {/* ══ PARENT'S DETAILS ═══════════════════════════════════════════ */}
          <SectionHdr>&#9632; PARENT'S DETAILS</SectionHdr>

          <FRow>
            <Lbl>Mother's Name</Lbl><Col /><Inp value={form.mother_name} onChange={set('mother_name')} />
          </FRow>
          <FRow>
            <Lbl>Father's Name</Lbl><Col /><Inp value={form.father_name} onChange={set('father_name')} />
            <Sp n={12} /><Lbl>Guardian Name</Lbl><Col /><Inp value={form.guardian_name} onChange={set('guardian_name')} />
          </FRow>
          <FRow>
            <Lbl>Qualification</Lbl><Col />
            <Chk label="Below SSC" checked={form.qualification === 'below_ssc'} onChange={toggle('qualification', 'below_ssc')} />
            <Sp /><Chk label="SSC" checked={form.qualification === 'ssc'} onChange={toggle('qualification', 'ssc')} />
            <Sp /><Chk label="Above SSC" checked={form.qualification === 'above_ssc'} onChange={toggle('qualification', 'above_ssc')} />
          </FRow>
          <FRow>
            <Lbl>Occupation</Lbl><Col /><Inp value={form.occupation} onChange={set('occupation')} />
            <Sp n={12} /><Lbl>Annual Income</Lbl><Col /><Inp value={form.annual_income} onChange={set('annual_income')} placeholder="₹" />
          </FRow>
          <FRow>
            <Lbl>Mobile No.</Lbl><Col /><Inp value={form.mobile} onChange={set('mobile')} placeholder="10-digit number" maxLength={10} />
            <Sp n={12} /><Lbl>Email</Lbl><Col /><Inp value={form.email} onChange={set('email')} placeholder="email@example.com" />
          </FRow>

          {/* ══ ADDRESS ════════════════════════════════════════════════════ */}
          <SectionHdr>&#9632; ADDRESS</SectionHdr>

          <SubHdr>Present Address</SubHdr>
          <FRow>
            <Lbl>H.No.</Lbl><Col /><Inp value={form.p_hno} onChange={set('p_hno')} style={{ width: 68, flex: 'none' }} />
            <Sp /><Lbl>Area</Lbl><Col /><Inp value={form.p_area} onChange={set('p_area')} style={{ width: 90, flex: 'none' }} />
            <Sp /><Lbl>Village</Lbl><Col /><Inp value={form.p_village} onChange={set('p_village')} />
          </FRow>
          <FRow>
            <Lbl>Mandal</Lbl><Col /><Inp value={form.p_mandal} onChange={set('p_mandal')} style={{ width: 82, flex: 'none' }} />
            <Sp /><Lbl>Dist.</Lbl><Col /><Inp value={form.p_dist} onChange={set('p_dist')} style={{ width: 82, flex: 'none' }} />
            <Sp /><Lbl>State</Lbl><Col /><Inp value={form.p_state} onChange={set('p_state')} style={{ width: 82, flex: 'none' }} />
            <Sp /><Lbl>Pin</Lbl><Col /><Inp value={form.p_pin} onChange={set('p_pin')} style={{ width: 56, flex: 'none' }} maxLength={6} />
          </FRow>

          <SubHdr>Permanent Address</SubHdr>
          <FRow>
            <Lbl>H.No.</Lbl><Col /><Inp value={form.pm_hno} onChange={set('pm_hno')} style={{ width: 68, flex: 'none' }} />
            <Sp /><Lbl>Area</Lbl><Col /><Inp value={form.pm_area} onChange={set('pm_area')} style={{ width: 90, flex: 'none' }} />
            <Sp /><Lbl>Village</Lbl><Col /><Inp value={form.pm_village} onChange={set('pm_village')} />
          </FRow>
          <FRow>
            <Lbl>Mandal</Lbl><Col /><Inp value={form.pm_mandal} onChange={set('pm_mandal')} style={{ width: 82, flex: 'none' }} />
            <Sp /><Lbl>Dist.</Lbl><Col /><Inp value={form.pm_dist} onChange={set('pm_dist')} style={{ width: 82, flex: 'none' }} />
            <Sp /><Lbl>State</Lbl><Col /><Inp value={form.pm_state} onChange={set('pm_state')} style={{ width: 82, flex: 'none' }} />
            <Sp /><Lbl>Pin</Lbl><Col /><Inp value={form.pm_pin} onChange={set('pm_pin')} style={{ width: 56, flex: 'none' }} maxLength={6} />
          </FRow>

          {/* Note + Signatures */}
          <div style={{ fontSize: 9, color: C.label, fontStyle: 'italic', margin: '7px 0 4px' }}>
            <strong>Note :</strong> I declared that the statement given above is true and correct to my best of knowledge.
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 14, fontSize: 10.5 }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ borderTop: `1px solid ${C.border}`, width: 200, margin: '20px 0 3px' }} />
              <span style={{ color: C.label }}>Signature of the Father / Mother / Guardian</span>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ borderTop: `1px solid ${C.border}`, width: 120, margin: '20px auto 3px' }} />
              <span style={{ color: C.label }}>Principal</span>
            </div>
          </div>

          {/* ══ FOR OFFICE USE ONLY ════════════════════════════════════════ */}
          <div style={{ border: `1.5px solid ${C.border}`, marginTop: 10, padding: '7px 10px' }}>
            <div style={{
              textAlign: 'center', fontWeight: 700, fontSize: 11, color: C.navy,
              borderBottom: `1px solid ${C.light}`, paddingBottom: 4, marginBottom: 6, letterSpacing: 1,
            }}>
              — FOR OFFICE USE ONLY —
            </div>

            <FRow>
              <Lbl>St. Name</Lbl><Col /><Inp value={form.o_st_name} onChange={set('o_st_name')} />
              <Sp n={10} /><Lbl>Class</Lbl><Col /><Inp value={form.o_class} onChange={set('o_class')} style={{ width: 52, flex: 'none' }} />
              <Sp n={10} /><Lbl>Admission No.</Lbl><Col /><Inp value={form.o_adm_no} onChange={set('o_adm_no')} style={{ width: 80, flex: 'none' }} />
            </FRow>
            <FRow style={{ marginTop: 3 }}>
              <Lbl>Receipt No.</Lbl><Col /><Inp value={form.o_receipt_no} onChange={set('o_receipt_no')} style={{ width: 90, flex: 'none' }} />
              <Sp /><Lbl>Date</Lbl><Col /><Inp value={form.o_date} onChange={set('o_date')} placeholder="DD/MM/YYYY" style={{ width: 90, flex: 'none' }} />
              <Sp /><Lbl>Amount</Lbl><Col /><Inp value={form.o_amount} onChange={set('o_amount')} placeholder="₹" />
            </FRow>
            <FRow style={{ marginTop: 3 }}>
              <Lbl>Referral Name</Lbl><Col /><Inp value={form.o_referral} onChange={set('o_referral')} style={{ width: 120, flex: 'none' }} />
              <Sp n={14} />
              <Chk label="Parent"       checked={form.o_referral_type === 'parent'}   onChange={toggle('o_referral_type', 'parent')} />
              <Sp /><Chk label="Staff"  checked={form.o_referral_type === 'staff'}    onChange={toggle('o_referral_type', 'staff')} />
              <Sp /><Chk label="Add Incharge" checked={form.o_referral_type === 'incharge'} onChange={toggle('o_referral_type', 'incharge')} />
            </FRow>

            <div style={{ marginTop: 5 }}>
              <span style={{ fontWeight: 600, fontSize: 10.5, color: C.navy }}>Certificates to be enclosed :</span>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 14px', marginTop: 4, marginLeft: 4 }}>
                <Chk label="Transfer Certificate"   checked={form.o_cert_tc}     onChange={tick('o_cert_tc')} />
                <Chk label="Record Sheet No. & Date" checked={form.o_cert_rs}    onChange={tick('o_cert_rs')} />
                <Chk label="Bonafide"                checked={form.o_cert_bon}   onChange={tick('o_cert_bon')} />
                <Chk label="Aadhar Card Xerox"       checked={form.o_cert_aadhar} onChange={tick('o_cert_aadhar')} />
                <Chk label="Birth Certificate"       checked={form.o_cert_birth} onChange={tick('o_cert_birth')} />
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12, fontSize: 10.5 }}>
              <div>
                <div style={{ borderTop: `1px solid ${C.border}`, width: 120, marginBottom: 3 }} />
                <span style={{ color: C.label }}>Incharge</span>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ borderTop: `1px solid ${C.border}`, width: 110, margin: '0 auto 3px' }} />
                <span style={{ color: C.label }}>Principal</span>
              </div>
            </div>
          </div>

        </div>
        {/* end form body */}
      </div>
      {/* end A4 card */}
    </div>
  );
}
